import { deaccent } from "./contentCategories";

/**
 * Recherche d'un TYPE de contenu dans l'admin.
 *
 * Le fonds compte des dizaines de types et le rédacteur ne connaît pas toujours leur titre
 * officiel : il tape le mot de son métier, une abréviation, ou il se trompe d'une lettre.
 * Une recherche par sous-chaîne stricte lui renvoyait alors une liste vide, sans lui dire
 * quoi essayer. Ce module ajoute trois tolérances, dans cet ordre de confiance :
 *
 *  1. sous-chaîne, le comportement d'avant, toujours prioritaire ;
 *  2. début de mot, pour que « comm » trouve « communiqués » ;
 *  3. UNE faute, pour que « comuniqué », « communiqé » ou « comminuqué » trouvent aussi.
 *
 * La tolérance à la faute ne s'applique qu'aux mots d'au moins quatre lettres : en dessous,
 * une lettre d'écart change le mot (« pdf » et « pif ») et ramènerait n'importe quoi.
 */

/** Longueur minimale d'un mot cherché pour accepter une faute. */
const MIN_FUZZY_LENGTH = 4;

/** Découpe en mots comparables : sans accents, sans ponctuation, sans vides. */
export const searchTokens = (value?: string | null): string[] =>
	deaccent(value)
		.split(/[^a-z0-9]+/)
		.filter(Boolean);

/**
 * Vrai quand une seule correction suffit à passer de `a` à `b` : une lettre ajoutée, retirée,
 * remplacée, ou deux lettres voisines interverties. L'interversion compte pour UNE faute, c'est
 * la coquille de frappe la plus courante (« inflaiton »), qu'une distance d'édition classique
 * facturerait deux.
 *
 * Écrit à la main plutôt qu'en programmation dynamique : borné à une faute, le test est linéaire
 * et se lit, là où une matrice complète coûterait sans rien apporter.
 */
export const withinOneEdit = (a: string, b: string): boolean => {
	if (a === b) return true;
	const diff = a.length - b.length;
	if (diff > 1 || diff < -1) return false;

	if (diff === 0) {
		let first = -1;
		let mismatches = 0;
		for (let i = 0; i < a.length; i += 1) {
			if (a[i] !== b[i]) {
				mismatches += 1;
				if (mismatches === 1) first = i;
				if (mismatches > 2) return false;
			}
		}
		if (mismatches <= 1) return true;
		// Exactement deux ecarts : acceptes seulement s'ils sont voisins et intervertis.
		return (
			mismatches === 2 &&
			a[first] === b[first + 1] &&
			a[first + 1] === b[first] &&
			a.slice(first + 2) === b.slice(first + 2)
		);
	}

	// Longueurs differentes d'une lettre : la plus longue doit se reduire a l'autre.
	const long = diff === 1 ? a : b;
	const short = diff === 1 ? b : a;
	let i = 0;
	let j = 0;
	let skipped = false;
	while (i < long.length && j < short.length) {
		if (long[i] === short[j]) {
			i += 1;
			j += 1;
			continue;
		}
		if (skipped) return false;
		skipped = true;
		i += 1;
	}
	return true;
};

/**
 * Vrai quand `token` retrouve `word` : debut de mot, mot entier a une faute pres, ou DEBUT de mot
 * a une faute pres. Ce dernier cas est indispensable : le redacteur tape « comunique » et le type
 * s'appelle « Communiqués » au pluriel, deux lettres d'ecart au total alors qu'il n'a fait qu'une
 * faute. On compare donc sa saisie au debut du mot, a sa propre longueur.
 */
const tokenMatchesWord = (token: string, word: string): boolean => {
	if (word.startsWith(token) || token.startsWith(word)) return true;
	if (token.length < MIN_FUZZY_LENGTH || word.length < MIN_FUZZY_LENGTH) return false;
	if (withinOneEdit(token, word)) return true;
	if (word.length <= token.length) return false;
	return (
		withinOneEdit(token, word.slice(0, token.length)) ||
		withinOneEdit(token, word.slice(0, token.length + 1))
	);
};

/**
 * Vrai quand la requête retrouve ce type. Chaque mot cherché doit être retrouvé quelque part
 * dans les textes fournis (titre, description, clé, mots-clés) : taper deux mots RESTREINT la
 * liste, il ne l'élargit pas.
 *
 * Une requête vide retourne vrai, la liste complète étant le bon repli.
 */
export const matchesContentTypeQuery = (haystack: Array<string | null | undefined>, query: string): boolean => {
	const wanted = searchTokens(query);
	if (wanted.length === 0) return true;

	const joined = deaccent(haystack.filter(Boolean).join(" "));
	const words = searchTokens(joined);

	return wanted.every((token) => {
		if (joined.includes(token)) return true;
		return words.some((word) => tokenMatchesWord(token, word));
	});
};
